(function () {
  const DRAWER_OPEN_DELAY_MS = 100;
  let previousProgress = 0;
  let pendingProgress = null;
  // The progress bar node, and the value, most recently animated. The drawer
  // replaces its children wholesale on every cart change (CartDrawer's
  // replaceContent in theme.js), so node identity is what tells us a swap has
  // actually landed.
  let settledProgressBar = null;
  let settledProgress = null;
  let idleTimeoutId = null;
  let fillDelayTimeoutId = null;

  function getShippingEligibleTotal(cart) {
    const itemTotal = cart.items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const cartDiscount = cart.cart_level_discount_applications.reduce(
      (sum, discount) => sum + discount.total_allocated_amount,
      0
    );

    return itemTotal - cartDiscount;
  }

  function getCartDrawer() {
    return document.querySelector("#cart-drawer");
  }

  function isCartDrawerOpen() {
    return getCartDrawer()?.hasAttribute("open") ?? false;
  }

  function getProgressBar() {
    return document.querySelector("#cart-drawer progress-bar.free-shipping-bar__progress");
  }

  function getFreeShippingBar() {
    return document.querySelector("#cart-drawer free-shipping-bar");
  }

  function destroyFreeShippingMessageAnimation() {
    const freeShippingBar = getFreeShippingBar();
    const message = freeShippingBar?.querySelector(".text-subdued");

    if (!freeShippingBar || !message) {
      return;
    }

    freeShippingBar.classList.remove("is-entering");
    message.style.animation = "none";
    message.style.opacity = "1";
    message.style.transform = "none";
    message.offsetHeight;
    message.style.animation = "";
    message.style.opacity = "";
    message.style.transform = "";
  }

  function startFreeShippingMessageAnimation() {
    const freeShippingBar = getFreeShippingBar();

    if (!freeShippingBar) {
      return;
    }

    destroyFreeShippingMessageAnimation();
    freeShippingBar.classList.add("is-entering");
  }

  function setProgressBarState(progressBar, state) {
    progressBar.classList.toggle("is-entering", state === "entering");
    progressBar.classList.toggle("is-updating", state === "updating");
    progressBar.classList.toggle("is-idle", state === "idle");
  }

  function markIdle(progressBar) {
    setProgressBarState(progressBar, "idle");
  }

  function clearAnimationTimeouts() {
    if (idleTimeoutId !== null) {
      window.clearTimeout(idleTimeoutId);
      idleTimeoutId = null;
    }

    if (fillDelayTimeoutId !== null) {
      window.clearTimeout(fillDelayTimeoutId);
      fillDelayTimeoutId = null;
    }
  }

  function scheduleIdle(progressBar, delayMs) {
    if (idleTimeoutId !== null) {
      window.clearTimeout(idleTimeoutId);
    }

    idleTimeoutId = window.setTimeout(() => {
      idleTimeoutId = null;
      markIdle(progressBar);
    }, delayMs);
  }

  function resetProgressBarInstant(progressBar) {
    progressBar.classList.remove("is-entering", "is-updating", "is-idle");
    progressBar.classList.add("is-resetting");
    progressBar.setAttribute("aria-valuenow", 0);
    progressBar.offsetHeight;
    progressBar.classList.remove("is-resetting");
    previousProgress = 0;
  }

  function animateProgressBar(progressBar, targetValue, { resetInstant = false, delayMs = 0 } = {}) {
    const max = parseFloat(progressBar.getAttribute("aria-valuemax"));
    const capped = Math.min(Math.max(0, targetValue), max);
    const fillDurationMs = 600;

    if (fillDelayTimeoutId !== null) {
      window.clearTimeout(fillDelayTimeoutId);
      fillDelayTimeoutId = null;
    }

    if (resetInstant) {
      resetProgressBarInstant(progressBar);
      setProgressBarState(progressBar, "entering");
    } else {
      setProgressBarState(progressBar, "updating");
      progressBar.setAttribute("aria-valuenow", previousProgress);
    }

    const startFill = () => {
      requestAnimationFrame(() => {
        progressBar.setAttribute("aria-valuenow", capped);
        previousProgress = capped;
      });
    };

    if (delayMs > 0) {
      fillDelayTimeoutId = window.setTimeout(() => {
        fillDelayTimeoutId = null;
        startFill();
      }, delayMs);
    } else {
      startFill();
    }

    scheduleIdle(progressBar, delayMs + fillDurationMs);
  }

  function markSettled(progressBar, target) {
    settledProgressBar = progressBar;
    settledProgress = target;
    pendingProgress = null;
  }

  function clearSettled() {
    settledProgressBar = null;
    settledProgress = null;
  }

  function applyPendingProgress() {
    if (!isCartDrawerOpen()) {
      return;
    }

    const progressBar = getProgressBar();

    if (!progressBar) {
      clearSettled();
      return;
    }

    const target = getProgressBarTarget(progressBar);

    if (target === null || Number.isNaN(target)) {
      return;
    }

    // Idempotent on purpose. The observer below fires for every mutation inside
    // the drawer, not just the content swap - the shipping-protection widget
    // re-injecting itself is enough - so this runs against the outgoing node as
    // well as the incoming one. Keying off node identity means an early call
    // against the node that is about to be discarded is a no-op instead of
    // consuming the update and leaving the fresh node at its rendered zero.
    if (progressBar === settledProgressBar && target === settledProgress) {
      return;
    }

    startFreeShippingMessageAnimation();
    animateProgressBar(progressBar, target);
    markSettled(progressBar, target);
  }

  function handleCartChange(event) {
    const cart = event.detail.cart;

    if (!cart || cart.item_count === 0) {
      previousProgress = 0;
      pendingProgress = null;
      clearSettled();
      return;
    }

    pendingProgress = getShippingEligibleTotal(cart);
  }

  // Safety net for the case where the drawer content swap produces no observable
  // mutation (an identical re-render): applyPendingProgress is idempotent, so a
  // redundant call here costs nothing.
  function scheduleApplyPendingProgress() {
    if (!isCartDrawerOpen()) {
      return;
    }

    requestAnimationFrame(() => {
      applyPendingProgress();
    });
  }

  document.addEventListener("cart:change", handleCartChange, { capture: true });

  document.addEventListener("cart:change", scheduleApplyPendingProgress);

  // `data-progress` is the server-rendered total for the node currently in the
  // DOM, so it is authoritative whenever it is present: after a content swap it
  // already carries the new cart total, and before one it still carries the old
  // one. `pendingProgress` is only a fallback for a bar rendered without it.
  function getProgressBarTarget(progressBar) {
    const dataProgress = progressBar.getAttribute("data-progress");

    if (dataProgress !== null && dataProgress !== "") {
      const parsed = parseFloat(dataProgress);

      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    if (pendingProgress !== null) {
      return pendingProgress;
    }

    return parseFloat(progressBar.getAttribute("aria-valuenow"));
  }

  function init() {
    const cartDrawer = getCartDrawer();

    if (!cartDrawer) {
      return;
    }

    const observer = new MutationObserver(() => {
      applyPendingProgress();
    });

    observer.observe(cartDrawer, { childList: true, subtree: true });

    cartDrawer.addEventListener("dialog:before-show", () => {
      destroyFreeShippingMessageAnimation();
    });

    cartDrawer.addEventListener("dialog:after-show", () => {
      startFreeShippingMessageAnimation();

      const progressBar = getProgressBar();

      if (!progressBar) {
        return;
      }

      const target = getProgressBarTarget(progressBar);

      markSettled(progressBar, target);

      animateProgressBar(progressBar, target, {
        resetInstant: true,
        delayMs: DRAWER_OPEN_DELAY_MS,
      });
    });

    cartDrawer.addEventListener("dialog:before-hide", () => {
      const progressBar = getProgressBar();

      clearAnimationTimeouts();
      destroyFreeShippingMessageAnimation();
      clearSettled();

      if (!progressBar) {
        return;
      }

      resetProgressBarInstant(progressBar);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
